import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Grid,
  Paper,
  TextField
} from '@mui/material';
import { make_generic_proxy_url } from '../app/utils.js';
import apiClient from '../app/apiClient.js';

/**
 * SymbologySelectorModal - A reusable modal component for selecting a symbology image (hatch or symbol)
 * 
 * @param {boolean} open - Controls the modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when an image is selected (receives the filename without extension)
 * @param {array} fileNames - List of file names (without .png extension)
 * @param {string} type - Type of symbology: "fill_pattern" or "symbols"
 * @param {string} title - Title of the modal
 */
function SymbologySelectorModal({ open, onClose, onSelect, fileNames = [], type, title = "Select symbology" }) {
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState(true);
  const [filterText, setFilterText] = useState('');

  // Shared styles
  const paperStyle = (isSelected) => ({
    p: 1,
    cursor: 'pointer',
    border: isSelected ? '2px solid' : '2px solid transparent',
    borderColor: isSelected ? 'primary.main' : 'transparent',
    '&:hover': { elevation: 4, borderColor: 'primary.light' },
    transition: 'all 0.2s',
  });
  
  const imageContainerStyle = {
    width: '100%',
    height: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    mb: 1,
    overflow: 'hidden',
    borderRadius: 1,
  };
  
  const labelStyle = (isSelected) => ({
    fontWeight: isSelected ? 'bold' : 'normal',
    color: isSelected ? 'primary.main' : 'text.primary',
  });

  // Load images when modal opens
  useEffect(() => {
    if (open && fileNames.length > 0) {
      setLoadingImages(true);
      setSelectedFileName(null);
      
      const authToken = apiClient.getAuthToken();
      if (!authToken) {
        console.error('No auth token available');
        setLoadingImages(false);
        return;
      }
      
      const loadImageUrl = (fileName) => {
        const s3Url = `s3://sites-orthophotos/symbology/${type}/${fileName}.png`;
        try {
          return make_generic_proxy_url(s3Url, "file", authToken);
        } catch (error) {
          console.error(`Error generating URL for ${fileName}:`, error);
          return null;
        }
      };

      const urls = {};
      fileNames.forEach(fileName => {
        const url = loadImageUrl(fileName);
        if (url) {
          urls[fileName] = url;
        }
      });
      
      setImageUrls(urls);
      setLoadingImages(false);
    }
  }, [open, fileNames, type]);

  const handleImageClick = (fileName) => {
    setSelectedFileName(fileName);
  };

  const handleSelect = () => {
    if (onSelect) {
      // Pass null if "None" is selected, otherwise pass the filename
      onSelect(selectedFileName);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedFileName(null);
    setFilterText('');
    if (onClose) {
      onClose();
    }
  };

  // Filter fileNames based on filterText
  const filteredFileNames = fileNames.filter(fileName => 
    fileName.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Filter input */}
          <TextField
            fullWidth
            label="Filter by name"
            variant="outlined"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Type to filter images..."
          />
          {fileNames.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                Hatches and symbols are not available for the moment
              </Typography>
            </Box>
          ) : loadingImages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredFileNames.length === 0 ? (
            <Typography color="text.secondary">
              No files match the filter
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ py: 2 }}>
              {/* None option */}
              <Grid item xs={6} sm={4} md={3}>
                <Paper
                  elevation={selectedFileName === null ? 6 : 2}
                  sx={paperStyle(selectedFileName === null)}
                  onClick={() => handleImageClick(null)}
                >
                  <Box sx={imageContainerStyle}>
                    <Typography variant="body1" color="text.secondary">None</Typography>
                  </Box>
                  <Typography variant="body2" align="center" sx={labelStyle(selectedFileName === null)}>
                    None
                  </Typography>
                </Paper>
              </Grid>
              {filteredFileNames.map((fileName) => (
                <Grid item xs={6} sm={4} md={3} key={fileName}>
                  <Paper
                    elevation={selectedFileName === fileName ? 6 : 2}
                    sx={paperStyle(selectedFileName === fileName)}
                    onClick={() => handleImageClick(fileName)}
                  >
                    <Box sx={imageContainerStyle}>
                      {imageUrls[fileName] ? (
                        <img
                          src={imageUrls[fileName]}
                          alt={fileName}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<Typography color="error">Failed to load</Typography>';
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                      )}
                    </Box>
                    <Typography variant="body2" align="center" sx={labelStyle(selectedFileName === fileName)}>
                      {fileName}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSelect} 
          variant="contained"
          disabled={selectedFileName === undefined || loadingImages}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SymbologySelectorModal;
